import React from 'react';
import { motion } from 'framer-motion';
import { useGclid } from '../src/hooks/useGclid';
import { buildShopUrl, trackCTAClick } from '../src/utils/tracking';
const SHOP_URL = 'https://hunderunde.shop/products/futtertopping-magen-darm';
export function DiarySection() {
  const gclid = useGclid();
  const href = buildShopUrl(SHOP_URL, gclid);
  return <section className="w-full px-4 py-8 max-w-3xl mx-auto space-y-12" data-id="element-45">
      {/* Entry 1 */}
      <motion.div initial={{
      opacity: 0,
      y: 30
    }} whileInView={{
      opacity: 1,
      y: 0
    }} viewport={{
      once: true,
      margin: '-100px'
    }} transition={{
      duration: 0.6
    }} className="bg-paper p-6 md:p-8 rounded-lg shadow-paper border border-brown/10 -rotate-1" data-id="element-46">
        <div className="border-b border-brown/20 pb-4 mb-4 flex flex-col md:flex-row md:items-baseline md:justify-between gap-2" data-id="element-47">
          <h3 className="font-handwriting text-3xl text-forest font-bold" data-id="element-48">
            Das Problem
          </h3>
          <span className="font-serif text-sm text-brown/60 italic" data-id="element-49">
            Vor 3,5 Monaten
          </span>
        </div>
        <div className="font-serif text-gray-700 leading-relaxed space-y-4 text-lg" data-id="element-50">
          <p data-id="element-51">
            Ben hatte eine Infektion. Antibiotika halfen schnell, aber danach
            fingen die Probleme erst richtig an:{' '}
            <strong data-id="element-52">wechselhafter Stuhl, Unruhe und schlechtes Fell.</strong>
          </p>
          <p data-id="element-53">
            Der Tierarzt erklärte mir, dass die Antibiotika seine{' '}
            <strong data-id="element-54">Darmflora komplett zerstört</strong> haben. "Das dauert
            Monate, bis sich das wieder aufbaut", sagte er.
          </p>
          <p data-id="element-55">
            <strong className="text-forest" data-id="element-56">
              Ich wusste nicht mehr weiter und konnte nicht einfach tatenlos
              zusehen, wie Ben leidet.
            </strong>
          </p>
        </div>
      </motion.div>

      {/* Entry 2 */}
      <motion.div initial={{
      opacity: 0,
      y: 30
    }} whileInView={{
      opacity: 1,
      y: 0
    }} viewport={{
      once: true,
      margin: '-100px'
    }} transition={{
      duration: 0.6,
      delay: 0.1
    }} className="bg-paper p-6 md:p-8 rounded-lg shadow-paper border border-brown/10 rotate-1" data-id="element-57">
        <div className="border-b border-brown/20 pb-4 mb-4 flex flex-col md:flex-row md:items-baseline md:justify-between gap-2" data-id="element-58">
          <h3 className="font-handwriting text-3xl text-forest font-bold" data-id="element-59">
            Der Wendepunkt
          </h3>
          <span className="font-serif text-sm text-brown/60 italic" data-id="element-60">
            Vor 11 Wochen
          </span>
        </div>
        <div className="font-serif text-gray-700 leading-relaxed space-y-4 text-lg" data-id="element-61">
          <p data-id="element-62">
            Ich verbrachte Nächte damit, alles über die Darmflora bei Hunden zu
            lesen. Dabei stieß ich auf ein{' '}
            <strong data-id="element-63">
              pflanzliches Futtertopping mit Inulin und Topinambur.
            </strong>
          </p>
          <p data-id="element-64">
            Ich hatte schon so viele Sachen ausprobiert — aber dann sah ich,
            dass fast 900 andere Hundehalter das Topping bewertet hatten, viele
            mit genau denselben Problemen wie Ben.
          </p>
          <p data-id="element-65">
            <strong className="text-forest" data-id="element-66">
              Wenn man verzweifelt ist, greift man nach jedem Strohhalm – also
              bestellte ich es sofort.
            </strong>
          </p>
        </div>
      </motion.div>

      {/* Pull Quote */}
      <motion.div initial={{
      opacity: 0
    }} whileInView={{
      opacity: 1
    }} viewport={{
      once: true
    }} className="py-8 px-4 text-center relative" data-id="element-67">
        <span className="absolute top-0 left-1/2 -translate-x-1/2 text-8xl font-serif text-brown/10 leading-none" data-id="element-68">
          "
        </span>
        <p className="font-serif text-2xl md:text-3xl italic text-forest/90 relative z-10 max-w-2xl mx-auto leading-relaxed" data-id="element-69">
          Kein Appetit, stumpfes Fell, ständige Müdigkeit – ich habe meinen Ben
          kaum wiedererkannt.
        </p>
      </motion.div>

      {/* Entry 3 */}
      <motion.div initial={{
      opacity: 0,
      y: 30
    }} whileInView={{
      opacity: 1,
      y: 0
    }} viewport={{
      once: true,
      margin: '-100px'
    }} transition={{
      duration: 0.6,
      delay: 0.2
    }} className="bg-paper p-6 md:p-8 rounded-lg shadow-paper border border-brown/10 -rotate-[0.5deg]" data-id="element-70">
        <div className="border-b border-brown/20 pb-4 mb-4 flex flex-col md:flex-row md:items-baseline md:justify-between gap-2" data-id="element-71">
          <h3 className="font-handwriting text-3xl text-forest font-bold" data-id="element-72">
            Die Veränderung
          </h3>
          <span className="font-serif text-sm text-brown/60 italic" data-id="element-73">
            Vor 7 Wochen
          </span>
        </div>
        <div className="font-serif text-gray-700 leading-relaxed space-y-4 text-lg" data-id="element-74">
          <p data-id="element-75">
            Schon in der zweiten Woche wurde sein Stuhl deutlich fester. In
            Woche 3 fraß Ben wieder mit richtigem Appetit und die Unruhe
            verschwand.
          </p>
          <p data-id="element-76">
            <strong className="text-forest" data-id="element-77">
              Nach 10 Tagen hatte Ben zum ersten Mal wieder normalen Stuhl und
              sein Bauch hat sich komplett beruhigt.
            </strong>
          </p>
          <p data-id="element-78">
            Heute glänzt sein Fell wieder und er tobt wie früher durch den
            Garten. Ich habe meinen Ben zurück.
          </p>
        </div>
      </motion.div>

      {/* Product Reveal Block */}
      <motion.div initial={{
      opacity: 0,
      y: 20
    }} whileInView={{
      opacity: 1,
      y: 0
    }} viewport={{
      once: true
    }} className="bg-white/60 p-6 md:p-8 rounded-2xl border border-sage flex flex-col md:flex-row gap-8 items-center mt-12" data-id="element-79">
        <div className="w-full md:w-1/2 rounded-xl overflow-hidden" data-id="element-80">
          <img src="https://cdn.magicpatterns.com/uploads/ue17e9YaKTG8qnsNfQnB2x/Magen_Darm_4f46689c-206a-426f-9344-0505df3c965a.webp" alt="Hunderunde Futtertopping Magen & Darm Verpackung" className="w-full h-auto object-contain" data-id="element-81" />
        </div>
        <div className="w-full md:w-1/2 space-y-4" data-id="element-82">
          <h4 className="font-handwriting text-3xl text-forest font-bold" data-id="element-83">
            Was ich bestellt habe
          </h4>
          <p className="font-serif text-gray-700 text-lg leading-relaxed" data-id="element-84">
            Ich habe mich bewusst für das Magen-Darm Topping von Hunderunde
            entschieden, weil es zu 100% pflanzlich ist und keine künstlichen
            Zusätze enthält. Die Kombination aus natürlichen Präbiotika wie
            Inulin und Topinambur war genau das, was Bens Darmflora für den
            Wiederaufbau brauchte.
          </p>
        </div>
      </motion.div>

      {/* CTA #1 */}
      <div className="pt-8 text-center" data-id="element-85">
        <a href={href} onClick={() => trackCTAClick('Das Topping das Ben geholfen hat → hier testen', 'story_cta')} target="_blank" rel="noopener noreferrer" className="inline-block w-full md:w-auto bg-forest text-cream font-serif font-bold text-lg px-8 py-4 rounded-full shadow-lg hover:scale-105 hover:bg-forest/90 transition-all duration-300" data-id="element-86">
          Das Topping das Ben geholfen hat → hier testen
        </a>
      </div>
    </section>;
}